import { type ReactNode } from "react";
import { formatPhone } from "shared/utils";
import { Show } from "shared/components";
import { AddressChip } from "./AddressChip";
import { distinctPhoneName } from "../utils/phoneLabel";

export interface MessageAddress {
  name: string;
  address: string;
}

export type MessageAddressVariant =
  | "default"
  // Pop-up floating card (2152:20753): 11px, name stacked over the number.
  | "compact-stacked"
  // Pop-up drawer: 11px single "name • number" line, truncated.
  | "compact-inline";

interface MessageAddressFieldProps {
  label: string;
  address: MessageAddress;
  variant?: MessageAddressVariant;
  // Rendered right after the number — used by the voice card for the redial
  // affordance the design puts on the To row.
  trailing?: ReactNode;
  /** Chips inside an outgoing bubble take the design's pink ground. */
  tinted?: boolean;
}

/**
 * The "From: / To:" header row shared by the SMS bubble and the voice call card.
 * Renders the display name (when known) and the number, separated by a dot.
 */
export const MessageAddressField = ({
  label,
  address,
  variant = "default",
  trailing,
  tinted
}: MessageAddressFieldProps) => {
  const name = distinctPhoneName(address.name, address.address);

  if (variant === "compact-stacked")
    return (
      <div className="flex items-start gap-1">
        <span className="w-8 shrink-0 font-medium text-content-1000">
          {label}
        </span>
        <span className="flex min-w-0 flex-col font-medium text-content-1000">
          <Show if={Boolean(name)}>
            <span className="truncate">{name}</span>
          </Show>
          <span className="flex min-w-0 items-center gap-1">
            <span className="truncate">{formatPhone(address.address)}</span>
            {trailing}
          </span>
        </span>
      </div>
    );

  if (variant === "compact-inline")
    return (
      <div className="flex items-center gap-1">
        <span className="w-8 shrink-0 font-medium text-content-1000">
          {label}
        </span>
        <Show if={Boolean(name)}>
          <span className="max-w-[300px] truncate font-medium text-content-1000">
            {name}
          </span>
          <div className="size-0.5 shrink-0 rounded-full bg-content-100" />
        </Show>
        <span className="max-w-[300px] truncate text-content-1000">
          {formatPhone(address.address)}
        </span>
        {trailing}
      </div>
    );

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="w-9 shrink-0 text-xs font-medium text-content-1000">
        {label}
      </span>
      <AddressChip
        address={formatPhone(address.address)}
        name={name}
        tinted={tinted}
      />
      {trailing}
    </div>
  );
};
